import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabaseCommentsRepository } from '../infrastructure/supabase-comments.repository';
import { type WorkoutComment } from '../domain/comment';

const repo = supabaseCommentsRepository;

const sessionCommentsKey = (sessionId: string) => ['comments', 'session', sessionId] as const;
const unreadKey = ['comments', 'unreadCount'] as const;

export function useSessionComments(
  sessionId: string,
): ReturnType<typeof useQuery<WorkoutComment[]>> {
  return useQuery({
    queryKey: sessionCommentsKey(sessionId),
    queryFn: () => repo.listForSession(sessionId),
  });
}

export function useUnreadCommentCount(): ReturnType<typeof useQuery<number>> {
  return useQuery({
    queryKey: unreadKey,
    queryFn: () => repo.countUnreadOnMine(),
  });
}

export function useCreateComment(
  sessionId: string,
): ReturnType<typeof useMutation<WorkoutComment, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => repo.create(sessionId, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionCommentsKey(sessionId) });
      void qc.invalidateQueries({ queryKey: unreadKey });
    },
  });
}

export function useMarkCommentsRead(): ReturnType<
  typeof useMutation<void, Error, readonly string[]>
> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids) => repo.markRead(ids),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: unreadKey });
    },
  });
}

export function useUpdateComment(
  sessionId: string,
): ReturnType<typeof useMutation<WorkoutComment, Error, { id: string; body: string }>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => repo.updateBody(id, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionCommentsKey(sessionId) });
    },
  });
}

export function useDeleteComment(
  sessionId: string,
): ReturnType<typeof useMutation<void, Error, string>> {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id) => repo.remove(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: sessionCommentsKey(sessionId) });
      void qc.invalidateQueries({ queryKey: unreadKey });
    },
  });
}
